import { CrmTaskStatus } from '@plato/data/prisma';
import { Link, useLocation } from 'react-router-dom';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { SalesOrderSearchRow } from '../../../server/types/search-types.ts';
import { SearchEntity, SearchEntityRoute } from '../../../server/types/search-types.ts';
import { EntitySearchCombobox } from '../../shared/components/form-items-with-fetch/entity-search-combobox.tsx';
import { useSearchEntityNavigation } from '../../shared/hooks/use-search-entity.ts';

import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { getDueDateColor } from '../../../utils/task-helpers.ts';

const salesOrderTypes = [SearchEntity.SalesOrder, SearchEntity.SalesQuote];

type TaskLinksInputProps = {
    value: CrmTaskInputDTO['links'];
    onChange: (value: CrmTaskInputDTO['links']) => void;
    taskId?: string;
    hideRelatedDetailsLink?: boolean;
};

export const TaskLinksInput = ({ value, onChange, taskId, hideRelatedDetailsLink }: TaskLinksInputProps) => {
    const { t } = useLocale('tasks');
    const { pathname } = useLocation();
    const { navigateToEntity } = useSearchEntityNavigation();

    const showGoToCustomerDetail = !pathname.includes('/customers/') && !hideRelatedDetailsLink;

    const watchedLink =
        value.find((link) => link.modelType !== (SearchEntity.Customer as string)) ||
        value.find((link) => link.modelType === (SearchEntity.Customer as string));

    const salesOrderValue = value.find((link) => salesOrderTypes.includes(link.modelType as SearchEntity));

    const defaultValue = (salesOrderValue ? [salesOrderValue] : value).map((v) => {
        const entity = v.modelType as SearchEntity;

        return {
            id: v.modelId,
            entity,
            name: v.title || '',
        };
    });

    return (
        <>
            <EntitySearchCombobox
                entities={[SearchEntity.Customer, SearchEntity.SalesOrder, SearchEntity.SalesQuote]}
                inputPlaceholder={t('tasks__entity_search__placeholder')}
                className="w-full"
                variant="inline"
                tracking={{
                    context: 'task_linked_entity_select',
                    context_entity_type: 'task',
                    context_entity_id: taskId,
                }}
                onValueChange={(values) => {
                    const [item] = values;

                    if (item) {
                        const newLinks = [
                            {
                                modelId: item.id,
                                modelType: item.entity,
                                title: item.name,
                            },
                        ];

                        if (salesOrderTypes.includes(item.entity)) {
                            const searchRow = item.original as SalesOrderSearchRow;

                            newLinks.push({
                                modelId: searchRow.customer_id,
                                modelType: SearchEntity.Customer,
                                title: searchRow.customer_name || '',
                            });
                        }

                        onChange(newLinks);
                    } else {
                        onChange([]);
                    }
                }}
                defaultValue={defaultValue}
            />
            {watchedLink && showGoToCustomerDetail && (
                <Link
                    onClick={(e) => {
                        e.preventDefault();

                        navigateToEntity({
                            entity: watchedLink.modelType as SearchEntity,
                            erp_id: watchedLink.modelId,
                        });

                        return null;
                    }}
                    to={`${SearchEntityRoute[watchedLink.modelType as keyof typeof SearchEntityRoute]}/${watchedLink.modelId}`}
                    className="text-sm font-medium text-blue-500 px-3"
                >
                    {t('show_details', { ns: 'common' })}
                </Link>
            )}
        </>
    );
};

type DueDatePickerProps = {
    value: Date;
    onChange: (value: Date | undefined) => void;
    taskStatus?: CrmTaskStatus;
};

export const DueDatePicker = ({ value, onChange, taskStatus }: DueDatePickerProps) => {
    return (
        <DatePicker
            disableIcon
            mode="single"
            value={value}
            onChange={onChange}
            textColor={
                taskStatus === CrmTaskStatus.COMPLETED ? 'neutral-700' : getDueDateColor({ dueDate: value })
            }
            disabled={{
                before: new Date(),
            }}
        />
    );
};
